import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCookie } from 'utils/bom-dom-manipulation/cookies';
import { login } from 'services/authentication';
import { useLoginCallback } from 'components/Login/hooks/useLoginCallback';
import { useAccountLogout } from 'hooks/auth/useAccountLogout';
import { modalAlert } from 'hooks/usePopup';

type ReturnType = {
  removeSecret: () => void;
  loginWithFaceId: () => void;
  setCredentials: (username: string, password: string) => void;
  showConfirmationPopup: (username: string, password: string) => void;
  credentialsExist: boolean;
};

export type TStatus = {
  hasTouchId: boolean;
  hasSecret: boolean;
};

type TGetAuth = {
  success: boolean;
  secret: string;
  error: string;
};

export const useFaceIdLogin = (): ReturnType => {
  const [credentialsExist, setCredentialsExist] = useState(false);
  const accountLogout = useAccountLogout();

  const { t } = useTranslation();

  const { loginCallback } = useLoginCallback({
    rememberMe: false,
    password: null,
    username: null
  });

  useEffect(() => {
    if (window.median?.auth) {
      window.median.auth.status({
        callbackFunction: (data: TStatus) => {
          if (data && data.hasTouchId && data.hasSecret) {
            setCredentialsExist(true);
          }
        }
      });
    }
  }, []);

  const removeSecret = () => {
    if (window.median?.auth) {
      window.median.auth.delete();
      setCredentialsExist(false);
    }
  };

  const loginWithFaceId = () => {
    if (window.median?.auth) {
      window.median.auth.status({
        callbackFunction: (data: TStatus) => {
          if (data && data.hasTouchId && data.hasSecret) {
            window.median.auth.get({
              callbackFunction: (res: TGetAuth) => {
                if (res && res.success && res.secret) {
                  const credentials = JSON.parse(res.secret);
                  const username = credentials.username;
                  const password = credentials.password;

                  if (username && password) {
                    login({ username, password }, loginCallback);
                  }
                } else if (res && res.error) {
                  accountLogout();
                } else {
                  accountLogout();
                }
              },
              callbackOnCancel: 'true'
            });
          }
        }
      });
    }
  };

  const setCredentials = (username: string, password: string) => {
    if (window.median?.auth) {
      removeSecret();
      window.median.auth.status({
        callbackFunction: (data: TStatus) => {
          if (data && data.hasTouchId) {
            const secret = JSON.stringify({
              username: username,
              password: password
            });

            window.median.auth.save({ secret: secret });
          }
        },
        prompt: 'PROMPT'
      });
    }
  };

  const showConfirmationPopup = (username: string, password: string): void => {
    if (window.median?.auth) {
      window.median.auth.status({
        callbackFunction: (data: TStatus) => {
          if (data && data.hasTouchId) {
            modalAlert({
              text: getCookie('isIOSWrapperApp')
                ? t('account.faceIdConfirmationInfoIos')
                : t('account.faceIdConfirmationInfoAndroid'),
              icon: {
                lib: 'account',
                name: getCookie('isIOSWrapperApp') ? 'FaceId' : 'Biometrics',
                theme: 'colored'
              },
              successBtn: {
                text: t('account.ok'),
                onClick: () => {
                  setCredentials(username, password);
                }
              },
              cancelBtn: {
                text: t('account.cancel')
              },
              closable: false
            });
          }
        },
        prompt: 'PROMPT'
      });
    }
  };

  return {
    removeSecret,
    loginWithFaceId,
    setCredentials,
    credentialsExist,
    showConfirmationPopup
  };
};
